import React from 'react';
import outcomeMobile from '../../../assets/images/rabbitX/outcome_mobile.webp';
import colors from '../../theme/color';

const OutcomeSection = () => {
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const pointerRef = React.useRef({ x: -9999, y: -9999 });

  const sendMouseToIframe = React.useCallback((x: number, y: number, active: boolean) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'outcome-mouse', x, y, active }, '*');
  }, []);

  React.useEffect(() => {
    const syncPointerToSection = () => {
      const section = sectionRef.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const { x: clientX, y: clientY } = pointerRef.current;
      const inside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (!inside) {
        sendMouseToIframe(-9999, -9999, false);
        return;
      }

      sendMouseToIframe(clientX - rect.left, clientY - rect.top, true);
    };

    const handleWindowMouseMove = (e: MouseEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      syncPointerToSection();
    };

    window.addEventListener('mousemove', handleWindowMouseMove, { passive: true });
    window.addEventListener('scroll', syncPointerToSection, { passive: true });
    window.addEventListener('resize', syncPointerToSection, { passive: true });

    // Keep effect synced even when iframe finishes loading after initial scroll.
    const iframe = iframeRef.current;
    iframe?.addEventListener('load', syncPointerToSection);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('scroll', syncPointerToSection);
      window.removeEventListener('resize', syncPointerToSection);
      iframe?.removeEventListener('load', syncPointerToSection);
    };
  }, [sendMouseToIframe]);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    pointerRef.current = { x: e.clientX, y: e.clientY };
    sendMouseToIframe(x, y, true);
  };

  const handleMouseLeave = () => {
    sendMouseToIframe(-9999, -9999, false);
  };

  return (
    <section
      ref={sectionRef}
      className="relative z-20 w-full overflow-hidden pb-8 md:pb-10 lg:pb-12 2xl:pb-14 min-[2560px]:pb-18"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Ambient glows */}
      <div
        className="pointer-events-none absolute -left-36 top-[20%] hidden h-[520px] w-[420px] rounded-full opacity-[0.07] blur-3xl md:block"
        style={{ backgroundColor: colors.rabbitXAccent }}
      />
      <div
        className="pointer-events-none absolute -right-24 top-[45%] hidden h-[480px] w-[360px] rounded-full opacity-[0.14] blur-3xl lg:block"
        style={{ backgroundColor: colors.rabbitXGlowPurple }}
      />

      {/* Background animation (from animation.html) */}
      <div
        className="absolute inset-x-0 top-0 z-0"
        style={{ height: '85%' }}
      >
        <iframe
          ref={iframeRef}
          src="/outcome-network.html"
          title="Outcome network background"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full border-0 opacity-[0.84]"
          style={{
            maskImage: 'linear-gradient(180deg, transparent 0%, black 12%, black 75%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, black 12%, black 75%, transparent 100%)',
          }}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1380px] px-5 md:px-8 lg:px-12 2xl:max-w-[2000px] 2xl:px-16 min-[2560px]:max-w-[2600px] min-[2560px]:px-24">
        {/* Outcome title */}
        <div className="mx-auto mb-8 flex w-full max-w-[1120px] items-center justify-center gap-4 pt-6 md:mb-10 md:gap-6 md:pt-10 2xl:mb-12 2xl:max-w-[1400px] 2xl:gap-8 2xl:pt-12 min-[2560px]:mb-16 min-[2560px]:max-w-[2000px] min-[2560px]:gap-12 min-[2560px]:pt-16">
          <span
            className="relative block h-px w-[30px] md:w-[68px] sm:w-[86px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}CC` }}
          >
            <span
              className="absolute -left-[4px] top-1/2 h-[5px] w-[5px] -translate-y-1/2 rounded-full"
              style={{ backgroundColor: `${colors.rabbitXAccent}F2` }}
            />
          </span>
          <span className="font-rajdhani text-[2rem] font-semibold leading-none text-white md:text-[3.3rem] 2xl:text-[4.2rem] min-[2560px]:text-[6rem]">Outcome</span>
          <span
            className="relative block h-px w-[30px] md:w-[68px] sm:w-[86px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}CC` }}
          >
            <span
              className="absolute -right-[4px] top-1/2 h-[5px] w-[5px] -translate-y-1/2 rounded-full"
              style={{ backgroundColor: `${colors.rabbitXAccent}F2` }}
            />
          </span>
        </div>

        {/* Phone image */}
        <div className="relative z-10 mx-auto flex h-[250px] w-full max-w-[1600px] items-end justify-center px-5 sm:h-[390px] md:h-[420px] md:px-8 lg:h-[640px] lg:px-12 2xl:h-[760px] min-[2560px]:h-[920px]">
          <img
            src={outcomeMobile}
            alt="RabbitX outcome mobile interface"
            className="w-full max-w-[320px] object-contain md:max-w-[450px] lg:max-w-[720px] 2xl:max-w-[880px] min-[2560px]:max-w-[1100px]"
          />
        </div>

        {/* Text block — sits directly below the phone, no gap */}
        <div
          className="relative z-10 mx-auto -mt-1 w-full max-w-[920px] overflow-hidden rounded-[14px] py-4 md:px-2 md:py-5 lg:px-10 lg:py-7 2xl:max-w-[1200px] 2xl:rounded-[22px] 2xl:px-14 2xl:py-10 min-[2560px]:max-w-[1600px] min-[2560px]:rounded-[32px] min-[2560px]:px-20 min-[2560px]:py-14"
          style={{
            backgroundImage: `linear-gradient(180deg, ${colors.rabbitXOutcomeCardA}75 0%, ${colors.rabbitXOutcomeCardB}EB 100%)`,
          }}
        >
          <div
            className="pointer-events-none absolute left-1/2 top-0 h-[120px] w-[74%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[90px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}73` }}
          />
          <p
            className="relative z-10 text-center font-gilroy-medium text-sm leading-[1.5] md:text-[1.06rem] md:leading-[1.58] lg:text-[1.18rem] 2xl:text-[1.45rem] 2xl:leading-[1.62] min-[2560px]:text-[2.1rem] min-[2560px]:leading-[1.65]"
            style={{ color: `${colors.rabbitXTextLight}CC` }}
          >
            RabbitX successfully delivers a streamlined and high-performance trading experience that caters to both
            beginner and professional traders. The intuitive interface, combined with real-time analytics and fast
            execution, enables users to make quicker and more informed decisions. Enhanced portfolio tracking provides
            clear visibility into performance and risk, while shareable P&amp;L features increase user engagement and
            social interaction. Overall, the platform improves usability, boosts retention, and positions RabbitX as a
            modern, user-centric solution in the competitive crypto trading space.
          </p>
        </div>
      </div>
    </section>
  );
};

export default OutcomeSection;
